import React, { useState } from 'react';

const CATEGORIES = ['Goat', 'Sheep', 'Bakra', 'Other'];
const BREEDS = ['Boer', 'Dorper', 'Merino', 'Kalahari Red', 'Angora', 'Damara', 'Saanen', 'Beetal', 'Sirohi', 'Other'];
const HEALTH_OPTIONS = [
  { value: 'healthy', label: 'Healthy — Vaccinated' },
  { value: 'good', label: 'Good Condition' },
  { value: 'needs_attention', label: 'Needs Attention' },
];

const inputClass = (hasErr) =>
  `w-full px-3 py-2 rounded-xl border bg-surface-container-low text-sm text-on-surface outline-none focus:ring-2 transition-all placeholder:text-outline-variant ${
    hasErr ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : 'border-outline-variant/40 focus:border-primary focus:ring-primary/10'
  }`;

const selectClass = `w-full px-3 py-2 rounded-xl border border-outline-variant/40 bg-surface-container-low text-sm outline-none focus:border-primary transition-all`;

export default function EditAnimalPanel({ animal, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    name:           animal.name          || '',
    category:       animal.category      || 'Goat',
    breed:          animal.breed         || '',
    description:    animal.description   || '',
    price_inr:      animal.price_inr     || '',
    weight_kg:      animal.weight_kg     || '',
    age_months:     animal.age_months    || '',
    teeth_count:    animal.teeth_count   || '',
    gender:         animal.gender        || 'Male',
    health_status:  animal.health_status || 'healthy',
    location_city:  animal.location_city || '',
    location_state: animal.location_state|| '',
    quantity:       animal.quantity      || 1,
  });

  const [errors, setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]   = useState(false);
  const [err, setErr]           = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const blockInvalidNumeric = (e) => {
    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
  };
  const blockDecimalAndInvalid = (e) => {
    if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault();
  };

  const handleSubmit = async () => {
    setErr('');
    const nameRegex   = /^[a-zA-Z0-9\s'-]{2,60}$/;
    const cityRegex   = /^[a-zA-Z\s'-]{2,50}$/;
    const positiveNum = (v) => /^\d+(\.\d+)?$/.test(String(v)) && Number(v) > 0;
    const positiveInt = (v) => /^\d+$/.test(String(v)) && Number(v) >= 1;

    const newErrors = {};
    if (!form.name.trim() || !nameRegex.test(form.name.trim()))
      newErrors.name = 'Name must be 2–60 chars, letters/numbers only';
    if (!form.price_inr || !positiveNum(form.price_inr))
      newErrors.price_inr = 'Enter a valid price (numbers only)';
    if (!form.location_city.trim() || !cityRegex.test(form.location_city.trim()))
      newErrors.location_city = 'Enter a valid city name (letters only)';
    if (!form.location_state.trim() || !cityRegex.test(form.location_state.trim()))
      newErrors.location_state = 'Enter a valid state name (letters only)';
    if (form.age_months !== '' && !positiveNum(form.age_months))
      newErrors.age_months = 'Must be a positive number';
    if (form.weight_kg !== '' && !positiveNum(form.weight_kg))
      newErrors.weight_kg = 'Must be a positive number';
    if (form.teeth_count !== '' && (!positiveInt(form.teeth_count) || Number(form.teeth_count) > 32))
      newErrors.teeth_count = 'Teeth must be 1–32';
    if (!positiveInt(form.quantity))
      newErrors.quantity = 'Quantity must be a whole number ≥ 1';

    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    setErrors({});
    setSubmitting(true);

    try {
      await onSubmit(animal.id, form);
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onCancel(); }, 1500);
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Failed to update.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">
        <div className="p-5 border-b border-surface-container-high flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
            <span className="material-symbols-outlined text-blue-600 text-[20px]">edit</span>
          </div>
          <div>
            <h3 className="font-bold text-base">Edit Animal</h3>
            <p className="text-[11px] text-secondary">Editing: {animal.name} — will go back to pending review</p>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
              <span className="material-symbols-outlined text-base">error</span>{err}
            </div>
          )}
          {success && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
              <span className="material-symbols-outlined text-base">check_circle</span>
              Updated successfully! Redirecting…
            </div>
          )}

          {/* Name + Price */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Animal Name *</label>
              <input className={inputClass(errors.name)} value={form.name}
                placeholder="e.g. Titan Buck" onChange={e => set('name', e.target.value)} />
              {errors.name && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors.name}</p>}
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Price (₹) *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary text-sm font-bold">₹</span>
                <input className={`${inputClass(errors.price_inr)} pl-7`} type="number"
                  value={form.price_inr} placeholder="45000"
                  onKeyDown={blockInvalidNumeric}
                  onChange={e => set('price_inr', e.target.value)} />
              </div>
              {errors.price_inr && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors.price_inr}</p>}
            </div>
          </div>

          {/* Category + Breed */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Category</label>
              <select className={selectClass} value={form.category} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Breed</label>
              <select className={selectClass} value={form.breed} onChange={e => set('breed', e.target.value)}>
                <option value="">Select breed</option>
                {BREEDS.map(b => <option key={b}>{b}</option>)}
              </select>
            </div>
          </div>

          {/* Age + Weight + Teeth */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Age (mo)</label>
              <input className={inputClass(errors.age_months)} type="number" value={form.age_months}
                placeholder="18" onKeyDown={blockInvalidNumeric} onChange={e => set('age_months', e.target.value)} />
              {errors.age_months && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors.age_months}</p>}
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Weight (kg)</label>
              <input className={inputClass(errors.weight_kg)} type="number" value={form.weight_kg}
                placeholder="45" onKeyDown={blockInvalidNumeric} onChange={e => set('weight_kg', e.target.value)} />
              {errors.weight_kg && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors.weight_kg}</p>}
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Teeth</label>
              <input className={inputClass(errors.teeth_count)} type="number" value={form.teeth_count}
                placeholder="4" onKeyDown={blockDecimalAndInvalid} onChange={e => set('teeth_count', e.target.value)} />
              {errors.teeth_count && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors.teeth_count}</p>}
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Qty</label>
              <input className={inputClass(errors.quantity)} type="number" min="1" value={form.quantity}
                placeholder="1" onKeyDown={blockDecimalAndInvalid} onChange={e => set('quantity', e.target.value)} />
              {errors.quantity && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors.quantity}</p>}
            </div>
          </div>

          {/* Gender + Health */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Gender</label>
              <select className={selectClass} value={form.gender} onChange={e => set('gender', e.target.value)}>
                <option value="Male">Male (Buck)</option>
                <option value="Female">Female (Doe)</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Health</label>
              <select className={selectClass} value={form.health_status} onChange={e => set('health_status', e.target.value)}>
                {HEALTH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {/* City + State */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">City *</label>
              <input className={inputClass(errors.location_city)} value={form.location_city}
                placeholder="e.g. Hyderabad" onChange={e => set('location_city', e.target.value)} />
              {errors.location_city && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors.location_city}</p>}
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">State *</label>
              <input className={inputClass(errors.location_state)} value={form.location_state}
                placeholder="e.g. Telangana" onChange={e => set('location_state', e.target.value)} />
              {errors.location_state && <p className="mt-0.5 text-[10px] text-red-500 font-semibold">{errors.location_state}</p>}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">Description</label>
            <textarea className={`${inputClass(false)} resize-none`} rows={2} value={form.description}
              placeholder="Describe the animal..." onChange={e => set('description', e.target.value)} />
          </div>

          {/* Note */}
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
            <span className="material-symbols-outlined text-amber-600 text-[16px]">info</span>
            <p className="text-[11px] text-amber-700 font-semibold">
              Saving will reset status to "Pending Review" for admin approval.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-3 justify-end border-t border-surface-container-high pt-4">
          <button onClick={onCancel}
            className="px-5 py-2.5 rounded-full border border-outline-variant text-secondary font-semibold text-sm hover:bg-surface-container transition-all">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            className="px-6 py-2.5 rounded-full bg-blue-600 text-white font-bold text-sm hover:opacity-90 transition-all disabled:opacity-60 flex items-center gap-2">
            {submitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[18px]">save</span>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}